/**
 * React hook for roster state management.
 *
 * Provides:
 * - Roster state access
 * - Loading states
 * - Error handling
 * - Automatic refetching
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient, lastOpSeq, opSeqOf, settledOpSeq } from '@/api/client';
import { adoptHandoffOverlap } from '@/utils/handoffOverlap';
import { adoptOfficerOrder } from '@/utils/officerOrder';
import { adoptHotoNotes } from '@/utils/hotoNotes';
import { adoptWhatsAppFooter } from '@/utils/whatsappFooter';
import { adoptDutyOptions } from '@/utils/dutyOptions';
import { adoptOtColorSettings } from '@/utils/otColors';
import { moveBlockInSnapshot } from '@/utils/blockMove';
import { coalesceAsync, mergeOwnEcho, shareSnapshot, shareState } from '@/utils/stateSync';
import { applyPatch, isWholeState, type StatePatch } from '@/utils/statePatch';
import { cachedVersion, rememberVersion } from '@/utils/versionStore';
import type { HistorySnapshot, OperationResult, RosterState } from '@/types/api';

export interface UseRosterStateReturn {
  state: RosterState | null;
  loading: boolean;
  error: string | null;
  /** Non-blocking error: set when a background refetch fails but state is already loaded. */
  transientError: string | null;
  refetch: () => Promise<void>;
  /** Directly apply a RosterState received from a WebSocket snapshot_update message. */
  applySnapshot: (snapshot: RosterState) => void;
  /**
   * Apply a snapshot another tab's edit caused (WebSocket). `mySeq` is the newest of
   * THIS tab's writes the snapshot includes (its `_seqs` entry for this tab). Unlike
   * applySnapshot it may be older than a grid this tab already holds from its own
   * edit's response — the socket and the response travel separately — and then the
   * grid is kept.
   */
  applyRemoteSnapshot: (snapshot: RosterState, mySeq?: number | null) => void;
  /**
   * Apply one revision step broadcast over the socket (see utils/statePatch): a patch
   * of the change only. Applied when it follows the revision held; an older one is
   * ignored; after a gap the state is fetched again and later steps wait for it.
   * Returns the grid before and after (for edit traces), or null when not applied now.
   */
  applyStateDelta: (delta: StateDelta, mySeq: number | null, own: boolean) => GridChange | null;
  /**
   * Apply this tab's OWN edit echoed back over the WebSocket. It reconciles
   * everything a background refetch used to (history, metadata, counts, …)
   * without fetching the state again; the grid is left to the edit's own
   * response (see mergeOwnEcho).
   */
  applyOwnEcho: (snapshot: RosterState, mySeq?: number | null) => void;
  /**
   * Instantly patch the current grid from a mutation's own response, instead of
   * waiting on a second round-trip. Every mutation endpoint already computes and
   * returns the up-to-date `current_snapshot` server-side, so this is not a guess —
   * it's the same data a refetch would eventually deliver, just without the wait.
   * Fields outside the grid (officer_metadata, officer_counts, history, …) are left
   * to the caller's own background refetch to reconcile.
   */
  patchFromOperation: (result: Pick<OperationResult, 'snapshot'> | undefined) => void;
  /**
   * Instantly jump the grid to an already-cached history entry (used for optimistic
   * undo/redo) — a version this tab has shown as current is kept (utils/versionStore),
   * so there's no need to wait on the undo/redo round trip just to show the change; one
   * it never had is drawn when the response lands. The real endpoint response still lands via applySnapshot
   * afterwards to reconcile officer_metadata/counts and confirm the server agrees.
   * Accepts either an absolute index or an updater keyed off the current pointer, so
   * back-to-back undo/redo clicks each step from the latest state rather than a value
   * captured in an earlier render's closure.
   */
  patchHistoryPointer: (next: number | ((current: number) => number)) => void;
  /**
   * Strip an officer out of the grid entirely, RIGHT NOW, before the removeOfficer
   * API call has even resolved — the round trip is what onSaved eventually
   * reconciles with (or a background refetch), not what the user should have to
   * sit through just to see the officer disappear. Returns a rollback to call if
   * that API call fails, restoring exactly what was there. A no-op call (nothing
   * to roll back to) returns a no-op rollback.
   */
  optimisticRemoveOfficer: (officerKeys: string | string[]) => () => void;
  /**
   * Same idea as optimisticRemoveOfficer, for "clear all counters" instead of a
   * full delete: empties the officer's counter assignments everywhere but keeps
   * their officer_schedules entry (as `[]`, not deleted) so the row/card stays on
   * the roster — matching what deleteOfficer actually does server-side. Accepts
   * one officer key or several so a bulk clear (see "Clear Counters" in the
   * Officers toolbar) lands as a single state update instead of one per officer.
   */
  optimisticClearOfficerCounters: (officerKeys: string | string[]) => () => void;
  /**
   * Same idea again, but for clearing only a chosen set of [startSlot, endSlot)
   * ranges — used when the "Clear Counters" picker clears specific assignment
   * windows instead of an officer's whole schedule.
   */
  optimisticClearOfficerRanges: (ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>) => () => void;
  /**
   * Move one officer's block from counter `from` to counter `to` (1-based) over
   * [startSlot, endSlot) on the grid RIGHT NOW, before the save returns. `rollback`
   * takes it back if the save fails; `sending` must be called right before the save's
   * request goes out (it may wait behind earlier moves).
   */
  optimisticMoveBlock: (move: OptimisticBlockMove) => { rollback: () => void; sending: () => void };
}

export interface StateDelta {
  base: number | null;
  rev: number;
  patch: StatePatch | null;
}

export interface GridChange {
  before: string[][][] | undefined;
  after: string[][][] | undefined;
}

/** Revision steps that arrived while the state was being fetched again (bounded). */
const MAX_BUFFERED_DELTAS = 200;

export interface OptimisticBlockMove {
  officerKey: string;
  from: number;
  to: number;
  startSlot: number;
  endSlot: number;
}

/**
 * A local edit drawn on the grid before the server has confirmed it. It is kept and
 * re-applied on top of every snapshot that arrives until one is known to include it,
 * so a snapshot that was already on its way when the edit was made — the save's own
 * slower sibling, or another user's broadcast — cannot wipe it off the grid (the
 * block that "jumped back" after a move). Every edit here is idempotent on a snapshot
 * that already has it, so re-applying is always safe.
 */
interface PendingEdit {
  id: number;
  apply: (snap: HistorySnapshot) => HistorySnapshot;
  /** The last X-Op-Seq sent before the edit's own write went out (so its write is the
   *  one after); null while that write is still waiting to be sent. */
  afterSeq: number | null;
  /** When the write went out (or the edit was made, until then). */
  since: number;
}

/** A pending edit no snapshot has accounted for in this long is dropped regardless
 *  (the socket was down, so the echo that would have cleared it never came). */
const PENDING_MAX_AGE_MS = 15_000;

function withPending(base: RosterState | null, pending: PendingEdit[]): RosterState | null {
  const snap = base?.current_snapshot;
  if (!base || !snap || pending.length === 0) return base;
  const next = pending.reduce((acc, p) => p.apply(acc), snap);
  return next === snap ? base : { ...base, current_snapshot: next };
}

/**
 * Hook to access and manage roster state
 */
export function useRosterState(): UseRosterStateReturn {
  const [state, setState] = useState<RosterState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transientError, setTransientError] = useState<string | null>(null);
  const isFirstFetch = useRef(true);
  // Tracks whether state has ever been loaded successfully — prevents
  // background refetch failures from wiping the UI.
  const hasState = useRef(false);
  // The server's state as last received, before local pending edits are laid over it.
  const baseRef = useRef<RosterState | null>(null);
  const pendingRef = useRef<PendingEdit[]>([]);
  const nextPendingId = useRef(0);
  // Bumped by every server state that arrives. A fetch whose response comes back after
  // a newer one was already applied is stale — painting it would put a just-moved
  // officer back on their old counter.
  const serverSeq = useRef(0);
  // The newest of this tab's writes whose effect the grid in baseRef is known to hold:
  // from its echo (`ackedSeq`) or from its own response (`patchedSeq`).
  const ackedSeq = useRef(0);
  const patchedSeq = useRef(0);
  // The revision baseRef is at (see utils/statePatch), and the steps that arrived out of
  // turn while it is fetched again.
  const revRef = useRef<number | null>(null);
  const bufferedDeltas = useRef<Array<{ delta: StateDelta; mySeq: number | null; own: boolean }>>([]);
  const outOfSync = useRef(false);
  const applyStateDeltaRef = useRef<UseRosterStateReturn['applyStateDelta']>(() => null);

  /** Show base + pending edits. Pending edits a snapshot is known to include, or that
   *  are too old to still be waiting on one, are dropped first. */
  const publish = useCallback(() => {
    const now = Date.now();
    pendingRef.current = pendingRef.current.filter(
      (p) => (p.afterSeq === null || p.afterSeq >= ackedSeq.current) && now - p.since < PENDING_MAX_AGE_MS);
    setState((prev) => {
      const next = withPending(baseRef.current, pendingRef.current);
      return next && prev ? shareState(prev, next) : next;
    });
  }, []);

  /** Take a full server state as the new base. `keepGrid` keeps the grid already held. */
  const setBase = useCallback((snapshot: RosterState, keepGrid = false) => {
    serverSeq.current += 1;
    hasState.current = true;
    adoptHandoffOverlap(snapshot.allow_handoff_overlap);
    adoptWhatsAppFooter(snapshot.whatsapp_footer);
    adoptDutyOptions(snapshot.duty_options);
    adoptOtColorSettings(snapshot.ot_color_settings);
    adoptOfficerOrder(snapshot.officer_order);
    adoptHotoNotes(snapshot.hoto_notes);
    baseRef.current = keepGrid ? mergeOwnEcho(baseRef.current, snapshot) : snapshot;
    if (typeof snapshot.rev === 'number' && (revRef.current === null || snapshot.rev >= revRef.current)) {
      revRef.current = snapshot.rev;
      outOfSync.current = false;
    }
    // Shown as current: kept, so an undo back to it needs nothing from the server.
    rememberVersion(snapshot.current_snapshot);
    publish();
    // Steps that waited for this state: those it does not already include, in order.
    if (!outOfSync.current && bufferedDeltas.current.length) {
      const waiting = bufferedDeltas.current.sort((a, b) => a.delta.rev - b.delta.rev);
      bufferedDeltas.current = [];
      for (const w of waiting) applyStateDeltaRef.current(w.delta, w.mySeq, w.own);
    }
    setError(null);
    setTransientError(null);
    isFirstFetch.current = false;
  }, [publish]);

  const fetchState = useCallback(async () => {
    const startedAt = ++serverSeq.current;
    // Every write of this tab already answered is in what the server returns (one
    // still in flight may land either side of this read).
    const coversSeq = settledOpSeq();
    try {
      // Only show full-page spinner on the initial load; background refetches are silent
      if (isFirstFetch.current) setLoading(true);
      if (isFirstFetch.current) setError(null);
      const data = await apiClient.getState();
      // Older than what is held: a revision applied since this read began has it all.
      // Taken anyway when the tab lost track (a gap in the revisions), which only a
      // fresh read can repair.
      const stale = typeof data.rev === 'number' && revRef.current !== null
        ? data.rev < revRef.current && !outOfSync.current
        : startedAt !== serverSeq.current && hasState.current;
      if (stale) return;
      ackedSeq.current = Math.max(ackedSeq.current, coversSeq);
      // A write's response newer than this read is already on screen: keep that grid.
      const newerGridHeld = patchedSeq.current > coversSeq;
      patchedSeq.current = Math.max(patchedSeq.current, coversSeq);
      setBase(data, newerGridHeld);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch state';
      if (!hasState.current) {
        // Fatal: no state loaded yet — surface the error so the UI can react
        setError(message);
      } else {
        // Soft: we have state — preserve it and show a dismissible banner instead
        setTransientError(message);
      }
      console.warn('Roster state fetch failed:', message);
    } finally {
      setLoading(false);
      isFirstFetch.current = false;
    }
  }, [setBase]);

  // Overlapping refetches (a burst of edits, a retry landing during a save) share
  // one follow-up fetch instead of each pulling the full state.
  const refetch = useMemo(() => coalesceAsync(fetchState), [fetchState]);

  /**
   * A snapshot off the socket. The socket delivers in the server's order, so it is the
   * newest server state there is — unless one of this tab's own later writes already
   * answered with a newer grid over HTTP. Then only the rest of the state is taken
   * (see mergeOwnEcho). `mySeq` (null from a server that does not send it) says which.
   */
  const applyBroadcast = useCallback((snapshot: RosterState, mySeq: number | null, own: boolean) => {
    if (mySeq === null) {
      // No way to tell: an own echo keeps the grid its response drew; another tab's
      // edit is taken whole.
      setBase(snapshot, own);
      return;
    }
    ackedSeq.current = Math.max(ackedSeq.current, mySeq);
    setBase(snapshot, mySeq < patchedSeq.current);
  }, [setBase]);

  const applyStateDelta = useCallback((delta: StateDelta, mySeq: number | null, own: boolean): GridChange | null => {
    const held = revRef.current;
    if (held !== null && delta.rev <= held) {
      // Already included (an undo's own response, a fresh read that overtook it).
      if (mySeq !== null) ackedSeq.current = Math.max(ackedSeq.current, mySeq);
      return null;
    }
    const base = baseRef.current;
    if (!isWholeState(delta.patch) && (!base || held === null || delta.base !== held)) {
      // A step is missing: read the state again; this one waits for it.
      outOfSync.current = true;
      bufferedDeltas.current.push({ delta, mySeq, own });
      if (bufferedDeltas.current.length > MAX_BUFFERED_DELTAS) bufferedDeltas.current.shift();
      void refetchRef.current();
      return null;
    }
    const next = { ...applyPatch(base, delta.patch) as RosterState, rev: delta.rev };
    const before = base?.current_snapshot?.counter_matrix;
    applyBroadcast(next, mySeq, own);
    return { before, after: baseRef.current?.current_snapshot?.counter_matrix };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  applyStateDeltaRef.current = applyStateDelta;

  const applyOwnEcho = useCallback(
    (snapshot: RosterState, mySeq?: number | null) => applyBroadcast(snapshot, mySeq ?? null, true),
    [applyBroadcast]);

  const applyRemoteSnapshot = useCallback(
    (snapshot: RosterState, mySeq?: number | null) => applyBroadcast(snapshot, mySeq ?? null, false),
    [applyBroadcast]);

  const applySnapshot = useCallback((snapshot: RosterState) => {
    const seq = opSeqOf(snapshot);
    if (seq !== null) patchedSeq.current = Math.max(patchedSeq.current, seq);
    setBase(snapshot);
  }, [setBase]);

  const patchFromOperation = useCallback((result: Pick<OperationResult, 'snapshot'> | undefined) => {
    if (!result?.snapshot || !baseRef.current) return;
    const seq = opSeqOf(result);
    // The echo of this write (or of a later one) already came over the socket, and the
    // socket also carried anything since: this response has nothing newer to add.
    if (seq !== null && seq <= ackedSeq.current) return;
    if (seq !== null) {
      // An older write answering after a newer one did is behind it, not ahead.
      if (seq < patchedSeq.current) return;
      patchedSeq.current = seq;
    }
    serverSeq.current += 1;
    baseRef.current = {
      ...baseRef.current,
      current_snapshot: shareSnapshot(baseRef.current.current_snapshot, result.snapshot),
    };
    publish();
  }, [publish]);

  /**
   * Lay an edit over the grid now. `rollback` takes it back off; `sending` records that
   * its write is going out now, for an edit whose write was not sent straight away.
   */
  const addPendingEdit = useCallback((apply: PendingEdit['apply'], sentNow: boolean) => {
    if (!baseRef.current?.current_snapshot) return { rollback: () => {}, sending: () => {} };
    const id = ++nextPendingId.current;
    const edit: PendingEdit = { id, apply, afterSeq: sentNow ? lastOpSeq() : null, since: Date.now() };
    pendingRef.current = [...pendingRef.current, edit];
    publish();
    return {
      rollback: () => {
        pendingRef.current = pendingRef.current.filter((p) => p.id !== id);
        publish();
      },
      sending: () => {
        edit.afterSeq = lastOpSeq();
        edit.since = Date.now();
      },
    };
  }, [publish]);
  const addPending = useCallback(
    (apply: PendingEdit['apply']): (() => void) => addPendingEdit(apply, true).rollback,
    [addPendingEdit]);

  const optimisticRemoveOfficer = useCallback((officerKeys: string | string[]): (() => void) => {
    const keys = new Set(Array.isArray(officerKeys) ? officerKeys : [officerKeys]);
    return addPending((snap) => {
      if (![...keys].some((k) => k in snap.officer_schedules)) return snap;
      const counter_matrix = snap.counter_matrix.map((counterSlots) =>
        counterSlots.map((slotOfficers) => slotOfficers.filter((o) => !keys.has(o))),
      );
      const officer_schedules = { ...snap.officer_schedules };
      for (const key of keys) delete officer_schedules[key];
      return { ...snap, counter_matrix, officer_schedules };
    });
  }, [addPending]);

  const optimisticClearOfficerCounters = useCallback((officerKeys: string | string[]): (() => void) => {
    const keys = new Set(Array.isArray(officerKeys) ? officerKeys : [officerKeys]);
    return addPending((snap) => {
      const counter_matrix = snap.counter_matrix.map((counterSlots) =>
        counterSlots.map((slotOfficers) => slotOfficers.filter((o) => !keys.has(o))),
      );
      const officer_schedules = { ...snap.officer_schedules };
      for (const key of keys) officer_schedules[key] = [];
      return { ...snap, counter_matrix, officer_schedules };
    });
  }, [addPending]);

  /**
   * Like optimisticClearOfficerCounters, but for clearing just PART of one or more
   * officers' schedules — a chosen set of [startSlot, endSlot) ranges — instead of
   * their whole schedule. Used by the "Clear Counters" picker, which lets a user
   * clear specific assignment windows rather than only all-or-nothing per officer.
   */
  const optimisticClearOfficerRanges = useCallback((
    ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>,
  ): (() => void) => {
    if (ranges.length === 0) return () => {};
    const byOfficer = new Map<string, Array<[number, number]>>();
    for (const r of ranges) {
      const list = byOfficer.get(r.officerKey) ?? [];
      list.push([r.startSlot, r.endSlot]);
      byOfficer.set(r.officerKey, list);
    }
    const clearedAt = (officerKey: string, slot: number) =>
      (byOfficer.get(officerKey) ?? []).some(([s, e]) => slot >= s && slot < e);
    return addPending((snap) => {
      const counter_matrix = snap.counter_matrix.map((counterSlots) =>
        counterSlots.map((slotOfficers, slotIdx) => slotOfficers.filter((o) => !clearedAt(o, slotIdx))),
      );
      const officer_schedules = { ...snap.officer_schedules };
      for (const [officerKey, officerRanges] of byOfficer) {
        const schedule = [...(officer_schedules[officerKey] ?? [])];
        for (const [startSlot, endSlot] of officerRanges) {
          for (let i = startSlot; i < endSlot && i < schedule.length; i++) schedule[i] = 0;
        }
        officer_schedules[officerKey] = schedule;
      }
      return { ...snap, counter_matrix, officer_schedules };
    });
  }, [addPending]);

  const optimisticMoveBlock = useCallback((move: OptimisticBlockMove) => {
    const { officerKey, from, to, startSlot, endSlot } = move;
    return addPendingEdit((snap) => moveBlockInSnapshot(snap, officerKey, from, to, startSlot, endSlot), false);
  }, [addPendingEdit]);

  const patchHistoryPointer = useCallback((next: number | ((current: number) => number)) => {
    const prev = baseRef.current;
    if (!prev) return;
    // Always derive from the latest state's pointer (not a value captured in the
    // caller's closure) so back-to-back clicks each step from wherever the previous
    // one actually landed, instead of racing on a stale index.
    const currentIndex = prev.history_index ?? prev.history.length - 1;
    const index = typeof next === 'function' ? next(currentIndex) : next;
    // Versions are headers here; the grid is the cached version, when this tab has it
    // (it was shown as current at some point). Otherwise the undo's response draws it.
    const snapshot = cachedVersion(prev.history?.[index]?.vid);
    if (!snapshot) return;
    serverSeq.current += 1;
    baseRef.current = { ...prev, history_index: index, current_snapshot: snapshot };
    publish();
  }, [publish]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Auto-retry on transient error (handles 502/503 gateway errors during deploys).
  // Waits 5 s then re-fetches silently so the user doesn't need to hit F5.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable refetch ref so the effect doesn't re-run when refetch identity changes.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (!transientError) {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return;
    }
    retryTimerRef.current = setTimeout(() => { void refetchRef.current(); }, 5000);
    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [transientError]);

  return { state, loading, error, transientError, refetch, applySnapshot, applyRemoteSnapshot, applyStateDelta, applyOwnEcho, patchFromOperation, patchHistoryPointer, optimisticRemoveOfficer, optimisticClearOfficerCounters, optimisticClearOfficerRanges, optimisticMoveBlock };
}
